import React, { createRef, memo, useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { VideoPlayerWrapper, VideoPlayerContainer } from "orion-components/CBComponents";
import { Button, Card, CardActions, CardContent, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { openDialog, closeDialog } from "orion-components/AppState/Dialog/actions";
import EmptySlot from "./EmptySlot";
import { v4 as uuidv4 } from "uuid";
import { setCameraPriority } from "orion-components/Dock/Actions/index.js";
import { selectCameraEntity } from "../../GlobalData/CameraClusters/selectors";
import { Cancel } from "@mui/icons-material";

const propTypes = {
	index: PropTypes.string,
	id: PropTypes.object,
	currentCol: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	currentRow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	columns: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	selectedClusterCameras: PropTypes.array,
	handleRemove: PropTypes.func,
	canAddCameras: PropTypes.bool,
	AddCameraToClusterDialog: PropTypes.func,
	clusterProps: PropTypes.object,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.string,
	isOnProfile: PropTypes.bool,
	canRemoveCameraFromSlot: PropTypes.bool
};

const defaultProps = {
	index: null,
	gridHeight: null,
	handleRemove: () => {
		return;
	},
	canAddCameras: false,
	AddCameraToClusterDialog: () => {
		return <></>;
	},
	clusterProps: {},
	isOnAlertProfile: false,
	isOnProfile: false,
	canRemoveCameraFromSlot: false
};

const getStyles = () => ({
	container: { backgroundColor: "#1F1F21", height: "100%", width: "100%" },
	cardActions: { padding: 0, justifyContent: "space-between" },
	cardContent: {
		padding: 0,
		height: "100%"
	}
});

const CameraSlot = ({
	index,
	currentCol,
	currentRow,
	selectedClusterCameras,
	handleRemove,
	canAddCameras,
	rows,
	columns,
	AddCameraToClusterDialog,
	clusterProps,
	isOnAlertProfile,
	alertDateTime,
	isOnProfile,
	canRemoveCameraFromSlot
}) => {
	const profile = useSelector(state => state.session.user.profile);
	const dock = useSelector(state => state.appState.dock);
	const Dialog = useSelector(state => state.appState.dialog);

	const instanceIdRef = useRef(uuidv4());

	const [styles, setStyles] = useState({});

	useEffect(() => {
		setStyles(getStyles());
	}, []);
	const { cameraPriority, dockedCameras } = dock.cameraDock;

	const cameraId = selectedClusterCameras?.[currentRow]?.[currentCol]?.cameraId ?? "";
	const dialogKey = `camera-wall-${cameraId ?? ""}`;
	const docked = cameraPriority.dockOpen && dockedCameras.includes(cameraId);

	// Create selector only when inputs change
	const selectCamera = useMemo(() => selectCameraEntity(cameraId, isOnAlertProfile), [cameraId, isOnAlertProfile]);

	// Get store data
	const cameraFromStore = useSelector(selectCamera);

	const camera = useMemo(() => {
		if (cameraFromStore === "Not available") return "Not available";
		return structuredClone ? structuredClone(cameraFromStore) : JSON.parse(JSON.stringify(cameraFromStore));
	}, [cameraFromStore]);

	const modal = Dialog?.openDialog === dialogKey || cameraPriority?.modalOpen;

	if (camera !== "Not available" && Object.values(camera).length !== 0) {
		if (selectedClusterCameras?.[currentRow]?.[currentCol]?.cameraMode === "recorded") {
			camera.cameraSlotProps = selectedClusterCameras?.[currentRow]?.[currentCol];
		}
	}

	const dialog = Dialog.openDialog;

	const dir = useSelector(state => getDir(state));
	const dispatch = useDispatch();
	const cardContainerRef = createRef();

	const handleOpenDialog = dialogKey => {
		dispatch(openDialog(dialogKey));
	};

	const handleCloseDialog = dialogKey => {
		dispatch(closeDialog(dialogKey));
	};

	const handleSetCameraPriority = (dockOpen, modalOpen) => {
		dispatch(setCameraPriority(dockOpen, modalOpen));
	};

	if (Object.values(camera).length !== 0 && camera !== "Not available") {
		const { entityData } = camera;
		const canControl =
			profile.integrations &&
			profile.integrations.find(int => int.intId === camera.feedId) &&
			profile.integrations.find(int => int.intId === camera.feedId).permissions &&
			profile.integrations.find(int => int.intId === camera.feedId).permissions.includes("control");

		return (
			<Card style={styles.container} ref={cardContainerRef}>
				{!camera.player?.newPlayer && (
					<CardActions style={styles.cardActions}>
						<Button
							color="primary"
							onClick={() => dispatch(openDialog(dialogKey))}
							style={{ textTransform: "none" }}
						>
							{entityData?.properties?.name ?? ""}
						</Button>
						{canRemoveCameraFromSlot && (
							<IconButton onClick={() => handleRemove(currentRow, currentCol)} style={{ padding: 8 }}>
								<Cancel />
							</IconButton>
						)}
					</CardActions>
				)}
				<CardContent style={styles.cardContent}>
					{camera.player?.newPlayer ? (
						<VideoPlayerContainer
							key={instanceIdRef.current}
							instanceId={instanceIdRef.current}
							camera={camera}
							canControl={canControl}
							closeDialog={handleCloseDialog}
							dialog={dialog}
							dialogKey={dialogKey}
							docked={docked}
							modal={modal}
							openDialog={handleOpenDialog}
							fillAvailable={true}
							dir={dir}
							expanded={false}
							isVideoWall={true}
							removeFromWall={handleRemove}
							setCameraPriority={handleSetCameraPriority}
							clusterProps={clusterProps}
							isOnAlertProfile={isOnAlertProfile}
							isOnProfile={isOnProfile}
							alertDateTime={alertDateTime}
							defaultToRecorded={camera.cameraSlotProps?.cameraMode === "recorded" ?? false}
							initialRecordedDuration={camera.cameraSlotProps?.duration}
							initialRecordedTimeUnit={camera.cameraSlotProps?.unitTime}
						/>
					) : (
						<VideoPlayerWrapper
							camera={camera}
							canControl={canControl}
							closeDialog={handleCloseDialog}
							dialog={dialog}
							dialogKey={dialogKey}
							docked={docked}
							modal={modal}
							openDialog={handleOpenDialog}
							fillAvailable={true}
							dir={dir}
							expanded={false}
							removeFromWall={handleRemove}
							setCameraPriority={handleSetCameraPriority}
						/>
					)}
				</CardContent>
			</Card>
		);
	} else {
		return (
			<EmptySlot
				key={index}
				camera={camera}
				canAddCameras={canAddCameras}
				rows={rows}
				columns={columns}
				currentCol={currentCol}
				currentRow={currentRow}
				AddCameraToClusterDialog={AddCameraToClusterDialog}
			/>
		);
	}
};

CameraSlot.propTypes = propTypes;
CameraSlot.defaultProps = defaultProps;

export default memo(CameraSlot);
